import React, { Fragment, useEffect, useState } from "react";
import useDeviceType from "../../Utiles/FindDevice";
import axios from "axios";
import { baseURL } from "../../services/axiosConfig";
import DownloadIcon from "../../Asserts/images/download_icon.svg"
import moment from "moment/moment";
import { refreshTokenV2 } from "../../services/api";
import { useDispatch } from "react-redux";

const CircularLoader = ({ isMobile = false }) => (
  <tr>
    <td colSpan="7" className="px-6 py-16 text-center">
      <div className="flex justify-center items-center">
        <div
          className={`animate-spin rounded-full border-b-2 border-blue-500 ${isMobile ? "h-8 w-8" : "h-12 w-12"}`}
        ></div>
      </div>
    </td>
  </tr>
);

const Pagination = ({ currentPage, totalPages, onPageChange, isMobile = false }) => (
  <div className={`flex items-center gap-1 ${isMobile ? "justify-end mt-2" : "justify-end mt-2"}`}>
    <button
      onClick={() => onPageChange(currentPage - 1)}
      disabled={currentPage === 1}
      className={`w-8 h-8 flex items-center justify-center ${isMobile ? "text-xs" : "text-sm"} text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 rounded border border-gray-200 bg-white`}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M7.5 9L4.5 6L7.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>

    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
      <button
        key={page}
        onClick={() => onPageChange(page)}
        className={`w-8 h-8 flex items-center justify-center ${isMobile ? "text-xs" : "text-sm"} font-medium rounded border transition-colors ${page === currentPage
          ? "bg-blue-500 text-white border-blue-500 shadow-sm"
          : "text-gray-600 hover:bg-gray-50 border-gray-200 bg-white"
          }`}
      >
        {page}
      </button>
    ))}

    <button
      onClick={() => onPageChange(currentPage + 1)}
      disabled={currentPage === totalPages}
      className={`w-8 h-8 flex items-center justify-center ${isMobile ? "text-xs" : "text-sm"} text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 rounded border border-gray-200 bg-white`}
    >
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  </div>
);

function DownloadButton({ onClick, isMobile = false }) {
  return (
    <button
      onClick={onClick}
      className={`text-blue-500 mx-auto hover:text-blue-700 font-medium ${isMobile ? 'text-xs' : 'text-sm'} flex items-center justify-center gap-1 hover:underline transition-colors`}
    >
      Download
      <img src={DownloadIcon} alt="Download" className={isMobile ? "w-3 h-3" : "w-4 h-4"} />
    </button>
  );
}

const MyInvoice = ({ dateRange }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [invoiceData, setInvoiceData] = useState([]);
  const [totalInvoice, setTotalInvoice] = useState(0);
  const [loading, setLoading] = useState(false);
  const deviceType = useDeviceType();
  const dispatch = useDispatch()
  const from = dateRange[0] ? moment(dateRange[0]).format("YYYY-MM-DD") : null;
  const to = dateRange[1] ? moment(dateRange[1]).format("YYYY-MM-DD") : null;

  const fetchInvoice = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("access_token");

      // build query params for page + filters
      const params = new URLSearchParams({
        page: currentPage.toString(),
      });
      if (from) params.append("from", from);
      if (to) params.append("to", to);

      const response = await axios.get(`${baseURL}/pot/invoices/?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${JSON.parse(token)}`,
        },
      });

      setInvoiceData(response.data.results || []);
      setTotalInvoice(response.data.count || 0);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [from, to]);

  // Fetch data when current page or filters change
  useEffect(() => {
    fetchInvoice();
  }, [currentPage, from, to]);


  const handleDownload = async (InvoiceNumber) => {
    try {
      const { data } = await axios.get(`${baseURL}/pot/invoices/${InvoiceNumber}/download/`, {
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem("access_token"))}`,
        },
        responseType: 'blob',
      });
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${InvoiceNumber}_receipt.pdf`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('PDF downloaded successfully');
    } catch (error) {
      console.error("Error downloading receipt:", error);
      if (error?.response?.status === 401) {
        await refreshTokenV2(dispatch);
        handleDownload(InvoiceNumber);
      } else {
        console.error("Error downloading receipt:", error);
      }
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [currentPage]);

  const totalPages = Math.ceil(totalInvoice / 10);

  const renderTableRows = () =>
    invoiceData.length > 0 ? (
      invoiceData.map((item) => (
        <tr key={item.id} className="hover:bg-slate-50">
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-left font-archivo">
            {new Date(item.created_at).toLocaleDateString("en-GB")}
          </td>
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-center font-archivo">
            {item.league_name}
          </td>
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-center font-archivo">
            {item.pot_id}
          </td>
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-center font-archivo">
            <span className="text-[16px]">$</span> {item.admin_fee}
          </td>
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-center font-archivo">
            <span className="text-[16px]">$</span> {item.vat_amount}
          </td>
          <td className="p-4 border-b border-slate-100 text-gray-700 text-sm text-center font-archivo">
            <span className="text-[16px]">$</span> {item.total_amount}
          </td>
          <td className="p-4 border-b border-slate-100 text-center">
            <DownloadButton
              onClick={() => handleDownload(item.id)}
              isMobile={true}
            />
          </td>
        </tr>
      ))
    ) : (
      <tr>
        <td
          colSpan="7" className="px-3 py-8 text-center text-gray-500 text-xl"
        >
          No invoice found
        </td>
      </tr>
    );


  return (
    <div className="min-h-screen bg-gray-50 px-[1%] mt-4">
      <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="w-full min-w-[800px]">
          <thead>
            <tr className="bg-gradient-to-r from-[#007fff] to-[#00c05b] text-white">
              <th className="px-3 py-3 text-left text-md font-semibold rounded-tl-lg font-archivo">Date</th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">League Name</th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">Round Id</th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">Admin Fee<div className="text-[12px]">(Inclusive of VAT)</div></th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">Vat Amount</th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">Total Amount</th>
              <th className="px-3 py-3 text-center text-md font-semibold font-archivo">Download Invoice</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? <CircularLoader isMobile={deviceType === "mobile"} /> : renderTableRows()}
          </tbody>
        </table>
      </div>

      {invoiceData.length > 0 && !loading && (
        <div className="p-4 bg-white border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            isMobile={deviceType === "mobile"}
          />
        </div>
      )}
    </div>
  );
};

export default MyInvoice;
